// typescript/lib/seavien-commons/src/tiptap/extension/ClipUtils.ts
//
// The URL shapes and attribute names for short-loop clips, in one place on the TypeScript side.
//
// A clip has exactly TWO objects and no size variants: `/clip/{id}/v` (the transcoded MP4) and
// `/clip/{id}/p` (the WebP poster). Both are written by `clip-transcoder-lambda.ts`, served by their
// own CloudFront behavior, and mirrored by `ClipUrlHelper.java`. None of those links is checked by a
// compiler, so they change together or not at all.
//
// Deliberately NOT reusing the image URL helpers. `/image/*` is fronted by a CloudFront Function that
// appends a `/m` size suffix to an un-suffixed URI, and `/backup/image/*` forces
// `Content-Disposition: attachment` — applied to a clip those request a variant that does not exist and
// turn playback into a download. There is no `getClipUrl(size)` to add here; adding one would be a
// request for a 404.
//
// Design: designs/01-now/2026-08-05-short-loop-video-upload-transcode-and-egress-guardrails-design.md

export const CLIP_NODE_TYPE = "clip";

/** Marks a poster `<img>` in stored body HTML as a clip. Presence is the whole signal. */
export const CLIP_MARKER_ATTRIBUTE = "data-seavien-clip";
export const CLIP_PUBLIC_ID_ATTRIBUTE = "data-clip-public-id";
export const CLIP_DURATION_MS_ATTRIBUTE = "data-clip-duration-ms";

/** Hard product ceiling, enforced authoritatively by the transcode Lambda's `TOO_LONG`. */
export const MAX_CLIP_DURATION_MS = 30_000;

/** The transcoded MP4. Requested only after an explicit play gesture — never linked from a page. */
export function getClipVideoUrl(clipPublicId: string | null | undefined): string {
	if (!clipPublicId) {
		return "";
	}
	return `/clip/${encodeURIComponent(clipPublicId)}/v`;
}

/**
 * The WebP poster frame — the URL that carries this feature's entire cost posture.
 *
 * It is what gets server-rendered into body HTML, what a feed card shows, and what a moderator
 * reviews instead of watching 30 seconds. With `preload="none"` on the player, an unwatched clip
 * costs exactly this one image and nothing else.
 */
export function getClipPosterUrl(clipPublicId: string | null | undefined): string {
	if (!clipPublicId) {
		return "";
	}
	return `/clip/${encodeURIComponent(clipPublicId)}/p`;
}

/** Recovers the public id from a poster URL, or null. Mirrors `ClipUrlHelper.clipPublicIdFromPosterUrl`. */
export function getClipPublicIdFromPosterUrl(url: string | null | undefined): string | null {
	if (!url) {
		return null;
	}
	let path = url;
	const schemeIndex = path.indexOf("://");
	if (schemeIndex >= 0) {
		const pathStart = path.indexOf("/", schemeIndex + 3);
		path = pathStart >= 0 ? path.slice(pathStart) : "/";
	}
	path = path.split("?")[0].split("#")[0];
	const match = /^\/?clip\/([^/]+)\/p\/?$/i.exec(path);
	if (!match) {
		return null;
	}
	try {
		return decodeURIComponent(match[1]);
	} catch {
		return match[1];
	}
}

/**
 * A clip's duration as `m:ss`, for the badge on a poster.
 *
 * Returns null when the duration is unknown, which is a real and ordinary state rather than an error:
 * the transcoder is what measures it, so a clip that is still processing (or whose transcode failed)
 * has none. Callers render no badge at all in that case — never `0:00`, which would read as an empty
 * clip rather than as a pending one.
 */
export function formatClipDuration(durationMs: number | null | undefined): string | null {
	if (typeof durationMs !== "number" || !Number.isFinite(durationMs) || durationMs <= 0) {
		return null;
	}
	const totalSeconds = Math.round(durationMs / 1000);
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}
